import { createStyles, Stack } from '@mantine/core';
import { useSuspenseGetAccessRequest } from '@repo/api-codegen';
import { UserRole } from '@repo/common/enums/UserRole';
import { Text, Title } from '@repo/foundations';
import { Helmet } from 'react-helmet';
import { useSuspenseFeatureAccess } from '../../api';
import { DataAccessRequestContent } from '../../components/DataAccessRequest/DataAccessRequestContent/DataAccessRequestContent';
import { DOCUMENTATION_WIDTH } from '../../components/Documentation/constants';
import Error404 from '../../components/Error/Error404';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import {
	PageLayoutContent,
	PageLayoutContentWrapper,
	PageLayoutOuterWrapper,
	PageLayoutWrapper,
} from '../../components/PageLayout';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';

const useStyles = createStyles((theme) => ({
	content: {
		maxWidth: DOCUMENTATION_WIDTH,
		marginTop: 0,
		marginBottom: 0,
	},
}));

export default function RequestPage() {
	const { classes } = useStyles();
	const id = useParamsIdSuffixUuid();
	const { darAccess } = useSuspenseFeatureAccess();

	const { data: request } = useSuspenseGetAccessRequest({
		pathParams: {
			requestId: id,
		},
	});

	if (!request) {
		return <Error404 />;
	}

	return (
		<FeatureGuard
			featureName="Data Access Requests"
			description="Request access and manage permissions to data in an end-to-end workflow."
			v1AllowedRoles={[
				UserRole.ADMIN,
				UserRole.EDITOR,
				UserRole.VIEWER,
				UserRole.GUEST,
			]}
			v2Permission="Integrations.Read"
			isFeatureIncludedInPlan={darAccess ?? false}
			isAlwaysIncludedFeature={false}
		>
			<PageLayoutOuterWrapper>
				<Helmet>
					<title>{request.integration_name} request</title>
				</Helmet>
				<PageLayoutWrapper name="request-page">
					<PageLayoutContentWrapper name="request-page">
						<PageLayoutContent className={classes.content}>
							<Stack spacing="xl" mb="0" pb="5xl">
								<Stack spacing="xs">
									<Title size="xl">{request.integration_name} request</Title>
									<Text size="md" style={{ whiteSpace: 'pre-wrap' }}>
										{request.requested_text}
									</Text>
								</Stack>
								<DataAccessRequestContent request={request} />
							</Stack>
						</PageLayoutContent>
					</PageLayoutContentWrapper>
				</PageLayoutWrapper>
			</PageLayoutOuterWrapper>
		</FeatureGuard>
	);
}
